import React, { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Typography,
  CircularProgress,
  Divider,
  IconButton,
  Alert,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  Close as CloseIcon,
  Google as GoogleIcon,
  GitHub as GitHubIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
} from "@mui/icons-material";
import { useFirebaseAuth } from "../../../hooks/useFirebaseAuth";
import { theme } from "../../../Themes/Themes";

const FirebaseAuth: React.FC = () => {
  const {
    user,
    loading,
    error,
    signInWithEmail,
    signUpWithEmail,
    signInWithGoogle,
    signInWithGitHub,
    signOutUser,
    resetPassword,
  } = useFirebaseAuth();

  const [showDialog, setShowDialog] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [localLoading, setLocalLoading] = useState(false);
  const [localError, setLocalError] = useState("");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleOpenDialog = () => {
    setShowDialog(true);
    setLocalError("");
    setResetEmailSent(false);
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
    setIsSignUp(false);
    setEmail("");
    setPassword("");
    setDisplayName("");
    setLocalError("");
    setResetEmailSent(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalLoading(true);
    setLocalError("");

    try {
      if (isSignUp) {
        await signUpWithEmail(email, password, displayName);
      } else {
        await signInWithEmail(email, password);
      }
      handleCloseDialog();
    } catch (error: any) {
      setLocalError(error.message);
    } finally {
      setLocalLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLocalLoading(true);
    setLocalError("");
    try {
      await signInWithGoogle();
      handleCloseDialog();
    } catch (error: any) {
      setLocalError(error.message);
    } finally {
      setLocalLoading(false);
    }
  };

  const handleGitHubSignIn = async () => {
    setLocalLoading(true);
    setLocalError("");
    try {
      await signInWithGitHub();
      handleCloseDialog();
    } catch (error: any) {
      setLocalError(error.message);
    } finally {
      setLocalLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!email) {
      setLocalError("Please enter your email address first");
      return;
    }

    setLocalLoading(true);
    setLocalError("");
    try {
      await resetPassword(email);
      setResetEmailSent(true);
    } catch (error: any) {
      setLocalError(error.message);
    } finally {
      setLocalLoading(false);
    }
  };

  const handleUserMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSignOut = async () => {
    try {
      await signOutUser();
      handleUserMenuClose();
    } catch (error: any) {
      console.error("Sign out error:", error);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="text.secondary">
          Loading...
        </Typography>
      </Box>
    );
  }

  if (user) {
    // User is signed in - show user menu
    return (
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <IconButton
          onClick={handleUserMenuClick}
          sx={{
            padding: "4px",
            "&:hover": {
              backgroundColor: "rgba(255, 174, 166, 0.1)",
            },
          }}
        >
          <Avatar
            src={user.photoURL || undefined}
            sx={{
              width: 32,
              height: 32,
              bgcolor: theme.custom.pink,
              fontSize: "14px",
            }}
          >
            {user.displayName?.charAt(0) || user.email?.charAt(0) || "U"}
          </Avatar>
        </IconButton>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleUserMenuClose}
          PaperProps={{
            sx: {
              mt: 1,
              minWidth: 200,
              borderRadius: "12px",
              border: `1px solid ${theme.custom.lightGrey}`,
            },
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1,
              borderBottom: `1px solid ${theme.custom.lightGrey}`,
            }}
          >
            <Typography variant="body2" fontWeight="bold">
              {user.displayName || "User"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {user.email}
            </Typography>
          </Box>

          <MenuItem onClick={handleSignOut}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Sign Out</ListItemText>
          </MenuItem>
        </Menu>
      </Box>
    );
  }

  // User is not signed in - show sign in button
  return (
    <>
      <Button
        variant="outlined"
        onClick={handleOpenDialog}
        sx={{
          borderRadius: "30px",
          borderColor: theme.custom.pink,
          color: theme.custom.pink,
          textTransform: "none",
          fontWeight: 600,
          px: 3,
          py: 1,
          "&:hover": {
            borderColor: theme.custom.pink,
            backgroundColor: "rgba(255, 174, 166, 0.1)",
          },
        }}
        startIcon={<PersonIcon />}
      >
        Sign In
      </Button>

      <Dialog
        open={showDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: "24px",
            width: "500px",
            paddingTop: "35px",
            position: "relative",
            overflow: "visible",
          },
        }}
      >
        {/* SDMaximize-style close button - positioned outside dialog */}
        <Box
          onClick={handleCloseDialog}
          sx={{
            position: "absolute",
            top: "25px",
            right: "-35px",
            height: "35px",
            width: "35px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 10001,
            cursor: "pointer",
            backgroundColor: theme.custom.pink,
            borderRadius: "0 50% 50% 0",
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: "#ff9999",
              transform: "scale(1.05)",
            },
          }}
        >
          <CloseIcon
            sx={{
              color: "white",
              fontSize: "18px",
            }}
          />
        </Box>

        <DialogTitle sx={{ pb: 1, pt: 2 }}></DialogTitle>

        <DialogContent sx={{ px: 8, py: 5 }}>
          {(error || localError) && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {localError || error}
            </Alert>
          )}

          {resetEmailSent && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Password reset email sent! Check your inbox.
            </Alert>
          )}

          {/* Social Sign In Buttons */}
          <Box sx={{ mb: 3 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleGoogleSignIn}
              disabled={localLoading}
              sx={{
                mb: 1,
                borderRadius: "30px",
                textTransform: "none",
                py: 1,
                borderColor: "#dadce0",
                color: "#3c4043",
                "&:hover": {
                  borderColor: "#dadce0",
                  backgroundColor: "#f8f9fa",
                },
              }}
              startIcon={<GoogleIcon />}
            >
              Continue with Google
            </Button>

            <Button
              fullWidth
              variant="outlined"
              onClick={handleGitHubSignIn}
              disabled={localLoading}
              sx={{
                borderRadius: "30px",
                textTransform: "none",
                py: 1,
                borderColor: "#d0d7de",
                color: "#24292f",
                "&:hover": {
                  borderColor: "#d0d7de",
                  backgroundColor: "#f6f8fa",
                },
              }}
              startIcon={<GitHubIcon />}
            >
              Continue with GitHub
            </Button>
          </Box>

          <Divider sx={{ my: 2 }}>
            <Typography variant="body2" color="text.secondary">
              or
            </Typography>
          </Divider>

          {/* Email/Password Form */}
          <Box component="form" onSubmit={handleSubmit}>
            {isSignUp && (
              <TextField
                fullWidth
                label="Full Name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                sx={{ mb: 2 }}
                InputProps={{
                  sx: {
                    borderRadius: "30px",
                    height: "48px",
                    "& .MuiInputBase-input": {
                      padding: "0 14px",
                      height: "48px !important",
                      lineHeight: "48px",
                    },
                  },
                }}
                InputLabelProps={{
                  sx: {
                    fontSize: "14px",
                    "&.MuiInputLabel-shrunk": {
                      fontSize: "12px",
                    },
                  },
                }}
              />
            )}

            <TextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              sx={{ mb: 2 }}
              InputProps={{
                sx: {
                  borderRadius: "30px",
                  height: "48px",
                  "& .MuiInputBase-input": {
                    padding: "0 14px",
                    height: "48px !important",
                    lineHeight: "48px",
                  },
                },
              }}
              InputLabelProps={{
                sx: {
                  fontSize: "14px",
                  "&.MuiInputLabel-shrunk": {
                    fontSize: "12px",
                  },
                },
              }}
            />

            <TextField
              fullWidth
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              sx={{ mb: 2 }}
              InputProps={{
                sx: {
                  borderRadius: "30px",
                  height: "48px",
                  "& .MuiInputBase-input": {
                    padding: "0 14px",
                    height: "48px !important",
                    lineHeight: "48px",
                  },
                },
              }}
              InputLabelProps={{
                sx: {
                  fontSize: "14px",
                  "&.MuiInputLabel-shrunk": {
                    fontSize: "12px",
                  },
                },
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={localLoading}
              sx={{
                borderRadius: "30px",
                backgroundColor: theme.custom.pink,
                color: "white",
                py: 1,
                textTransform: "none",
                fontWeight: 600,
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: theme.custom.pink,
                  opacity: 0.9,
                  boxShadow: "none",
                },
              }}
            >
              {localLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : isSignUp ? (
                "Create Account"
              ) : (
                "Sign In"
              )}
            </Button>
          </Box>

          {/* Toggle Sign Up/Sign In */}
          <Box sx={{ textAlign: "center", mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {isSignUp ? "Already have an account?" : "Don't have an account?"}{" "}
              <Button
                variant="text"
                onClick={() => {
                  setIsSignUp(!isSignUp);
                  setLocalError("");
                  setResetEmailSent(false);
                }}
                sx={{
                  textTransform: "none",
                  color: theme.custom.pink,
                  fontWeight: 600,
                  p: 0,
                  minWidth: "auto",
                }}
              >
                {isSignUp ? "Sign In" : "Sign Up"}
              </Button>
            </Typography>
          </Box>

          {/* Forgot Password */}
          {!isSignUp && (
            <Box sx={{ textAlign: "center", mt: 1 }}>
              <Button
                variant="text"
                onClick={handlePasswordReset}
                disabled={localLoading}
                sx={{
                  textTransform: "none",
                  color: theme.custom.pink,
                  fontSize: "12px",
                  p: 0,
                  minWidth: "auto",
                }}
              >
                Forgot password?
              </Button>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FirebaseAuth;
